import { useState, useEffect, useMemo, Fragment } from 'react';
import {
  collection, onSnapshot, doc, getDocs, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { ShortNoPill } from '../components/IdPill';
import { shortInvoiceNo } from '../utils/invoiceNo';
import { Button, ConfirmModal, DeleteIconButton } from '../components/ui';
import RecordAdvanceModal from '../components/RecordAdvanceModal';
import ApplyAdvanceModal, { type ApplyInvoice } from '../components/ApplyAdvanceModal';
import { HeaderSearch, DateRangeButton, StatChips, IconPlusSm } from '../components/HeaderControls';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import { computeDateWindow, inWindow, type DateFilterKey } from '../utils/ledgerReports';
import { entryId } from '../utils/ledger';
import type { CustomerAdvance, AdvanceApplication, SaleInvoice, Entity, BankAccount, PaymentStatus } from '../types';

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const fmtDate = (s?: string) =>
  s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const outstandingOf = (inv: SaleInvoice) =>
  round2(Math.max(0, inv.totalAmountUSD - (inv.paidUSD ?? 0) - (inv.creditedUSD ?? 0)));

// ── Page size — personal, per-browser preference (never BusinessSettings) ──
const PAGE_SIZE = 50;

// ── Pagination (same pattern as Purchase/Sales/Inventory/Entity/Memos/AP/AR/SalesReturns) ──
function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);
  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3)         pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }
  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> advance{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

export default function CustomerAdvancesPage() {
  const { business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [advances, setAdvances] = useState<CustomerAdvance[]>([]);
  const [sales, setSales]       = useState<SaleInvoice[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading]   = useState(true);

  const [dateKey, setDateKey] = useState<DateFilterKey>('this-year');
  const [searchQ, setSearchQ] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [recording, setRecording] = useState(false);
  const [applyFor, setApplyFor]   = useState<CustomerAdvance | null>(null);
  const [confirmDel, setConfirmDel] = useState<CustomerAdvance | null>(null);
  const [busy, setBusy] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [apps, setApps] = useState<Record<string, AdvanceApplication[]>>({});

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const u1 = onSnapshot(collection(db, 'customerAdvances'),
      snap => { setAdvances(snap.docs.map(d => ({ id: d.id, ...d.data() } as CustomerAdvance))); setLoading(false); },
      () => setLoading(false));
    const u2 = onSnapshot(collection(db, 'sales'),
      snap => setSales(snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice))), () => {});
    const u3 = onSnapshot(collection(db, 'entities'),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))), () => {});
    const u4 = onSnapshot(collection(db, 'bankAccounts'),
      snap => setAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount)).filter(a => a.isActive !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))), () => {});
    return () => { u1(); u2(); u3(); u4(); };
  }, [business]);

  const entityName = (id: string) => entities.find(e => e.id === id)?.companyName ?? '—';
  const window = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);
  const rows = useMemo(() => {
    if (!window) return [];
    const q = searchQ.trim().toLowerCase();
    return advances
      .filter(a => inWindow(a.date, window))
      .filter(a => !q || [a.advanceNo, entityName(a.toEntityId)].some(v => (v ?? '').toLowerCase().includes(q)))
      .sort((a, b) => b.date.localeCompare(a.date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advances, window, searchQ, entities]);

  useEffect(() => { setCurrentPage(1); }, [dateKey, customFrom, customTo, searchQ]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const totalRemaining = useMemo(
    () => round2(advances.reduce((s, a) => s + Math.max(0, (a.amountUSD ?? 0) - (a.appliedUSD ?? 0)), 0)),
    [advances],
  );

  const openInvoicesFor = (customerId: string): ApplyInvoice[] =>
    sales.filter(i => i.toEntityId === customerId && outstandingOf(i) > 0.005)
      .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''))
      .map(i => ({ id: i.id, invoiceNo: i.invoiceNo, totalAmountUSD: i.totalAmountUSD, paidUSD: i.paidUSD, creditedUSD: i.creditedUSD, saleDate: i.saleDate, dueDate: i.dueDate }));

  async function toggleExpand(adv: CustomerAdvance) {
    if (expanded === adv.id) { setExpanded(null); return; }
    setExpanded(adv.id);
    if (!apps[adv.id]) {
      const db = getClientDb();
      if (!db) return;
      const snap = await getDocs(collection(db, 'customerAdvances', adv.id, 'applications'));
      setApps(a => ({ ...a, [adv.id]: snap.docs.map(d => ({ id: d.id, ...d.data() } as AdvanceApplication)) }));
    }
  }

  async function doDelete(adv: CustomerAdvance) {
    setBusy(true);
    const db = getClientDb();
    if (!db) { setBusy(false); return; }
    try {
      const accRef = adv.bankAccountId ? doc(db, 'bankAccounts', adv.bankAccountId) : null;
      await runTransaction(db, async tx => {
        const accSnap = accRef ? await tx.get(accRef) : null;
        tx.delete(doc(db, 'customerAdvances', adv.id));
        if (adv.bankTransactionId) tx.delete(doc(db, 'bankTransactions', adv.bankTransactionId));
        if (accRef && accSnap?.exists()) {
          tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance - (adv.amountUSD ?? 0) });
        }
        tx.delete(doc(db, 'journalEntries', entryId('customer_advance', adv.id)));
      });
    } finally { setBusy(false); setConfirmDel(null); }
  }

  async function unApply(adv: CustomerAdvance, ap: AdvanceApplication) {
    setBusy(true);
    const db = getClientDb();
    if (!db || !business) { setBusy(false); return; }
    try {
      const advRef = doc(db, 'customerAdvances', adv.id);
      const invRef = doc(db, 'sales', ap.invoiceId);
      await runTransaction(db, async tx => {
        const advSnap = await tx.get(advRef);
        const invSnap = await tx.get(invRef);
        if (advSnap.exists()) {
          const a = advSnap.data() as CustomerAdvance;
          const newApplied = round2(Math.max(0, (a.appliedUSD ?? 0) - (ap.amountUSD ?? 0)));
          tx.update(advRef, {
            appliedUSD: newApplied,
            status: newApplied >= (a.amountUSD ?? 0) - 0.005 ? 'used' : newApplied > 0 ? 'partial' : 'open',
            updatedAt: serverTimestamp(),
          });
        }
        if (invSnap.exists()) {
          const inv = invSnap.data() as SaleInvoice;
          const newPaid = round2(Math.max(0, (inv.paidUSD ?? 0) - (ap.amountUSD ?? 0)));
          const settled = newPaid + (inv.creditedUSD ?? 0);
          const status: PaymentStatus =
            settled >= inv.totalAmountUSD - 0.005 ? 'paid' : settled > 0 ? 'partial' : 'unpaid';
          tx.update(invRef, { paidUSD: newPaid, paymentStatus: status, updatedAt: serverTimestamp() });
        }
        tx.delete(doc(db, 'customerAdvances', adv.id, 'applications', ap.id));
        tx.delete(doc(db, 'journalEntries', entryId('customer_advance_apply', ap.id)));
      });
      setApps(a => ({ ...a, [adv.id]: (a[adv.id] ?? []).filter(x => x.id !== ap.id) }));
    } finally { setBusy(false); }
  }

  return (
    <AppLayout
      title="Customer Advances"
      subtitle="Money a customer paid before an invoice — apply it to sales later."
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Advance no, customer…" label="Search advances" />
        <DateRangeButton
          value={dateKey} onChange={setDateKey} defaultKey="this-year"
          customFrom={customFrom} customTo={customTo}
          onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
        />
        {(searchQ || dateKey !== 'this-year') && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); setDateKey('this-year'); }}>Clear</button>
        )}
      </>}
      headerActions={
        <Button variant="primary" size="sm" icon={<IconPlusSm />} onClick={() => setRecording(true)} disabled={!business}
          style={{ height: 34, boxSizing: 'border-box' }}>New Advance</Button>
      }
    >
      <StatChips items={[
        { label: 'Unapplied', value: money(totalRemaining), tone: 'primary' },
        { label: 'Open', value: String(advances.filter(a => (a.amountUSD ?? 0) - (a.appliedUSD ?? 0) > 0.005).length) },
      ]} />

      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No advances here</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Record one when a customer pays up front.</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 30 }}></th>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 120 }}>Advance No</th>
                  <th>Customer</th>
                  <th style={{ textAlign: 'right', width: 110 }}>Amount</th>
                  <th style={{ textAlign: 'right', width: 110 }}>Remaining</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map(a => {
                  const rem = round2((a.amountUSD ?? 0) - (a.appliedUSD ?? 0));
                  const open = expanded === a.id;
                  return (
                    <Fragment key={a.id}>
                      <tr onClick={() => toggleExpand(a)} style={{ cursor: 'pointer' }}>
                        <td style={{ width: 40 }}>
                          {/* Bigger, clearer open / close for the "applied to" list. */}
                          <span className={`row-expand${open ? ' open' : ''}`} title={open ? 'Hide where it was applied' : 'Show where it was applied'}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                          </span>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{fmtDate(a.date)}</td>
                        <td><ShortNoPill value={a.advanceNo} /></td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{entityName(a.toEntityId)}</td>
                        <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-num)', fontWeight: 600 }}>{money(a.amountUSD)}</td>
                        <td style={{ textAlign: 'right', fontSize: 12, fontFamily: 'var(--font-num)', color: rem > 0.005 ? 'var(--primary)' : 'var(--text-3)' }}>{money(rem)}</td>
                        <td className="col-actions" onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {rem > 0.005 && <Button variant="secondary" size="sm" onClick={() => setApplyFor(a)}>Apply</Button>}
                            {(a.appliedUSD ?? 0) <= 0.005 && <DeleteIconButton onClick={() => setConfirmDel(a)} />}
                          </div>
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td></td>
                          <td colSpan={6} style={{ padding: 0 }}>
                            <div style={{ background: 'var(--surface-2)', padding: '8px 14px' }}>
                              {(apps[a.id] ?? []).length === 0 ? (
                                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Not applied to anything yet.</div>
                              ) : (apps[a.id] ?? []).map(ap => (
                                <div key={ap.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0', fontSize: 12, color: 'var(--text-2)' }}>
                                  <span className="sm-ref" title={ap.invoiceNo}>{shortInvoiceNo(ap.invoiceNo)}</span>
                                  <span style={{ color: 'var(--text-3)' }}>{fmtDate(ap.date)}</span>
                                  <span style={{ fontFamily: 'var(--font-num)', marginLeft: 'auto' }}>{money(ap.amountUSD)}</span>
                                  <Button variant="ghost" size="sm" loading={busy} onClick={() => unApply(a, ap)}>Un-apply</Button>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <Pagination
          current={currentPage} total={totalPages}
          totalItems={rows.length} pageSize={PAGE_SIZE}
          onChange={setCurrentPage}
        />
      )}

      {recording && (
        <RecordAdvanceModal kind="customer" entities={entities} accounts={accounts} onClose={() => setRecording(false)} />
      )}
      {applyFor && (
        <ApplyAdvanceModal
          kind="customer" advance={applyFor}
          invoices={openInvoicesFor(applyFor.toEntityId)}
          partyName={entityName(applyFor.toEntityId)}
          onClose={() => { setApplyFor(null); setApps(a => { const n = { ...a }; delete n[applyFor.id]; return n; }); }}
        />
      )}
      {confirmDel && (
        <ConfirmModal
          open title="Delete this advance?"
          message={`${confirmDel.advanceNo} (${money(confirmDel.amountUSD)}) will be removed and taken back out of ${confirmDel.bankAccountName ?? 'the account'}.`}
          confirmLabel="Delete" loading={busy}
          onConfirm={() => doDelete(confirmDel)} onClose={() => setConfirmDel(null)}
        />
      )}
    </AppLayout>
  );
}
